import React, { useState } from 'react';
import DeleteCascadeModal from './DeleteCascadeModal';
import {
    BatchDeleteAction,
    BatchItem,
    BatchResource,
    BATCH_DELETE_MAX,
    batchDelete,
    batchPublish,
    fetchBatchDeletePreview,
    BlockedTarget,
} from '../api/batch';
import { DeleteCascadePayload, RelatedGroup } from '../api/cascade';
import { alertApprovalIfPending, ApiError } from '../api/request';
import { BatchSelect } from '../utils/useBatchSelect';
import { useCan } from '../store';
import { Resource, rbacResourceOf } from '../api/authz';
import { confirmDialog, alertDialog } from '../utils/dialog';

// 列表页批量操作按钮 (2026-09-10): 放在页头按钮区, 与「导出」「批量导入」同排。
// 提供批量删除 (所有列表) 与批量发布 / 取消发布 (材料、设备)。
//
// 按钮**常驻**, 未勾选时置灰 —— 与页头其他按钮一样始终在同一个位置, 用户一眼知道
// 这个列表支持批量操作; 条件渲染会让整排按钮在勾选时左右跳动。已选条数直接写进
// 按钮文案 ("批量删除 (3)"), 省掉一条单独的计数栏。
//
// 删除弹窗直接复用单条删除那套 DeleteCascadeModal —— 批量预检返回的是**合并后**的同一份
// RelatedGroup 结构 (同 key 累加), 所以勾选交互、阻塞禁用、审批工单里的影响面展示全都一致,
// 用户不用学第二套语义。

interface Props {
    resource: BatchResource;
    /** 选中的目标编码 (调用方从 useBatchSelect 拿) */
    selected: string[];
    onClear: () => void;
    /** 操作提交后回读列表 (走审批时数据未变, 回读只是拿回原值) */
    onDone: () => void | Promise<void>;
    /** 资源中文名, 用于文案: "批量删除 3 个材料" */
    entityLabel: string;
    /**
     * 单条删除请求体的业务键名 (如 'xcode' / 'deviceKey' / 'code')。
     * 批量删除的每一项都按 {[deleteKeyField]: targetCode, cascade, related} 组请求体,
     * 与该资源单条删除接口收的是同一个结构 —— 审批回放走的就是它原有的 ApplyChange。
     */
    deleteKeyField: string;
    /** 键不是单字段的资源 (如参数行 archCode+id) 用它自定义请求体; 传了则忽略 deleteKeyField */
    buildDeleteData?: (targetCode: string) => Record<string, unknown>;
    /**
     * 材料 / 设备: 显示批量发布 / 取消发布。按钮可见性走 RBAC (2026-09-11):
     * 发布 = 该资源的 publish 权, 删除 = 该资源的 delete 权 —— 与后端 /v1/batch/* 在
     * handler 里按请求体 resource 判定的口径一致 (authz.Require), 提交仍走审批。
     */
    publishable?: boolean;
    /**
     * 某项当前是否已发布 (publishable 时传)。批量发布/取消发布据此**只提交状态需要变的那些项** ——
     * 后端 SetPublish 对"已是目标状态"的会幂等短路, 不传的话选中一堆已发布的点「批量发布」
     * 会生成一张审完什么都没做的空工单, 白耗一次审批。
     */
    publishedOf?: (targetCode: string) => boolean;
    /** 删除动作 (SKU 走 delete_sku, 解绑走 unbind_sku); 缺省 delete */
    deleteAction?: BatchDeleteAction;
    /**
     * 不走审批工单的资源用它 (目前只有 API Token: 删除即时生效, 有 owner 与"必须先吊销"两道校验,
     * 没有回放器也就进不了批量工单)。给了它就逐条调用并汇总失败, 同时跳过级联预检。
     */
    localDelete?: (targetCode: string) => Promise<unknown>;
}

function BatchActions({
    resource,
    selected,
    onClear,
    onDone,
    entityLabel,
    deleteKeyField,
    buildDeleteData,
    publishable,
    publishedOf,
    deleteAction,
    localDelete,
}: Props) {
    // 资源名与后端 authz 词表同字面值; 唯一例外 apitoken (前端本地删除的伪资源) → RBAC 域 api_token
    const authzResource: Resource =
        resource === 'apitoken' ? 'api_token' : rbacResourceOf(resource);
    const canPublish = useCan(authzResource, 'publish');
    const canDelete = useCan(authzResource, 'delete');
    const [delOpen, setDelOpen] = useState(false);
    const [blocked, setBlocked] = useState<BlockedTarget[]>([]);
    const [busy, setBusy] = useState(false);

    const none = selected.length === 0;
    const overflow = selected.length > BATCH_DELETE_MAX;
    const count = none ? '' : ` (${selected.length})`;
    // 未勾选时的统一提示: 说清楚"要先勾什么", 而不是只给个灰按钮
    const emptyHint = `请先在列表里勾选要操作的${entityLabel}`;

    // 发布/取消发布只作用于**状态需要变的**那些项: 混选时自动跳过已是目标状态的,
    // 按钮上的数字就是真正会提交的条数 (没传 publishedOf 则不过滤, 全量提交).
    const targetsFor = (publish: boolean) =>
        publishedOf
            ? selected.filter((c) => publishedOf(c) !== publish)
            : selected;
    const skippedHint = (publish: boolean, n: number) => {
        const skipped = selected.length - n;
        if (skipped <= 0) return '';
        return ` (已跳过 ${skipped} 项${publish ? '已发布' : '未发布'}的)`;
    };

    const loadPreview = async (): Promise<RelatedGroup[]> => {
        const res = await fetchBatchDeletePreview(resource, selected);
        setBlocked(res.blocked || []);
        return res.groups || [];
    };

    // 本地删除: 逐条调该资源自己的删除接口, 汇总失败明细抛回弹窗 (它会原样展示).
    const confirmLocalDelete = async () => {
        const failed: string[] = [];
        for (const code of selected) {
            try {
                await localDelete!(code);
            } catch (e) {
                failed.push(
                    `${code}: ${e instanceof ApiError ? e.message : String(e)}`,
                );
            }
        }
        onClear();
        await onDone();
        if (failed.length > 0) {
            throw new Error(
                `${selected.length - failed.length} 项已删除, ${failed.length} 项失败 — ${failed.join('; ')}`,
            );
        }
    };

    const confirmDelete = async (payload: DeleteCascadePayload) => {
        const items: BatchItem[] = selected.map((code) => ({
            targetCode: code,
            data: {
                ...(buildDeleteData
                    ? buildDeleteData(code)
                    : { [deleteKeyField]: code }),
                cascade: payload.cascade,
                related: payload.related,
            },
        }));
        const res = await batchDelete(resource, items, deleteAction);
        onClear();
        await onDone();
        alertApprovalIfPending(res.message);
    };

    const runPublish = async (publish: boolean) => {
        const targets = targetsFor(publish);
        if (targets.length === 0) return;
        const msg = publish
            ? `确认发布 ${targets.length} 个${entityLabel}${skippedHint(publish, targets.length)}?\n发布后 OpenAPI 对下游可见 (参数行还需所属设备与引用材料同时已发布).`
            : `确认取消发布 ${targets.length} 个${entityLabel}${skippedHint(publish, targets.length)}?\n取消后 OpenAPI 不再下发这些数据及其关联参数行; 平台内展示/编辑不受影响.`;
        if (!await confirmDialog(msg)) return;
        setBusy(true);
        try {
            const res = await batchPublish(
                resource as 'material' | 'device',
                targets,
                publish,
            );
            onClear();
            await onDone();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(
                `操作失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`,
            );
        } finally {
            setBusy(false);
        }
    };

    const buttons = (
        <>
            {publishable && canPublish && (
                <>
                    <PublishButton
                        label="批量发布"
                        className="text-emerald-600"
                        busy={busy}
                        selectedCount={selected.length}
                        targets={targetsFor(true)}
                        emptyHint={emptyHint}
                        allDoneHint={`勾选的${entityLabel}都已发布`}
                        onClick={() => runPublish(true)}
                    />
                    <PublishButton
                        label="批量取消发布"
                        className="text-slate-500"
                        busy={busy}
                        selectedCount={selected.length}
                        targets={targetsFor(false)}
                        emptyHint={emptyHint}
                        allDoneHint={`勾选的${entityLabel}都未发布`}
                        onClick={() => runPublish(false)}
                    />
                </>
            )}
            {canDelete && (
                <button
                    type="button"
                    className="btn-ghost text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={busy || none || overflow}
                    title={
                        none
                            ? emptyHint
                            : overflow
                              ? `批量删除单次最多 ${BATCH_DELETE_MAX} 项 (需逐项预检关联数据), 当前已选 ${selected.length} 项`
                              : '批量删除勾选项, 提交后走审批'
                    }
                    onClick={() => setDelOpen(true)}
                >
                    批量删除{count}
                </button>
            )}
        </>
    );

    return (
        <>
            {buttons}

            <DeleteCascadeModal
                open={delOpen}
                title={`批量删除${entityLabel}`}
                targetLabel={
                    <>
                        选中的 {selected.length} 个{entityLabel}
                        <span className="ml-1 font-mono text-xs text-slate-400">
                            ({selected.slice(0, 5).join('、')}
                            {selected.length > 5
                                ? ` 等 ${selected.length} 项`
                                : ''}
                            )
                        </span>
                    </>
                }
                loadPreview={localDelete ? undefined : loadPreview}
                warning={
                    blocked.length > 0 ? (
                        <>
                            以下 {blocked.length} 项存在阻塞引用,
                            需先解除才能删除 (整批不会执行):
                            <span className="mt-1 block font-mono">
                                {blocked
                                    .slice(0, 10)
                                    .map(
                                        (b) =>
                                            `${b.targetCode} — ${b.reasons.join('、')}`,
                                    )
                                    .join('; ')}
                                {blocked.length > 10
                                    ? ` … 等 ${blocked.length} 项`
                                    : ''}
                            </span>
                        </>
                    ) : (
                        <>
                            勾选对整批统一生效: 每个{entityLabel}
                            都按同一套口径处理关联数据。
                            审批通过后在同一事务里逐个执行, 任一项失败整批回滚。
                        </>
                    )
                }
                onConfirm={localDelete ? confirmLocalDelete : confirmDelete}
                onClose={() => setDelOpen(false)}
            />
        </>
    );
}

/**
 * 发布 / 取消发布按钮: 数字是**真正会提交的条数**(状态已经是目标值的自动跳过)。
 * 勾选项全都无需改动时按钮置灰 —— 后端 SetPublish 会幂等短路, 提交只会换来
 * 一张审完什么都没做的空工单。
 */
function PublishButton({
    label,
    className,
    busy,
    selectedCount,
    targets,
    emptyHint,
    allDoneHint,
    onClick,
}: {
    label: string;
    className: string;
    busy: boolean;
    selectedCount: number;
    targets: string[];
    emptyHint: string;
    allDoneHint: string;
    onClick: () => void;
}) {
    const skipped = selectedCount - targets.length;
    const title =
        selectedCount === 0
            ? emptyHint
            : targets.length === 0
              ? allDoneHint
              : skipped > 0
                ? `只提交状态需要变的 ${targets.length} 项, 另外 ${skipped} 项已是该状态; 提交后走审批`
                : `${label}勾选项, 提交后走审批`;
    return (
        <button
            type="button"
            className={`btn-ghost ${className} disabled:opacity-40 disabled:cursor-not-allowed`}
            disabled={busy || targets.length === 0}
            title={title}
            onClick={onClick}
        >
            {label}
            {targets.length > 0 ? ` (${targets.length})` : ''}
        </button>
    );
}

export default BatchActions;

/** 表头全选框: 半选态用 indeterminate (原生属性只能用 ref 设, 所以不是纯 className). */
export function HeaderCheckbox({
    sel,
    title,
    hidden,
}: {
    sel: BatchSelect;
    title?: string;
    /** 无批量操作权限时不渲染 (RBAC, 2026-09-12): 勾了也没有可执行的动作 */
    hidden?: boolean;
}) {
    if (hidden) return null;
    return (
        <input
            type="checkbox"
            className="cursor-pointer align-middle"
            title={title || '全选本页'}
            checked={sel.allChecked}
            ref={(el) => {
                if (el) el.indeterminate = sel.someChecked;
            }}
            onChange={sel.toggleAll}
        />
    );
}

/** 行选择框. k = 该行的业务键 (与批量接口的 targetCode 同一个值). */
export function RowCheckbox({ sel, k, hidden }: { sel: BatchSelect; k: string; hidden?: boolean }) {
    if (hidden) return null;
    return (
        <input
            type="checkbox"
            className="cursor-pointer align-middle"
            checked={sel.isSelected(k)}
            onChange={() => sel.toggle(k)}
            onClick={(e) => e.stopPropagation()}
        />
    );
}
